/**
 * Two distinct bits of browser state:
 *   1. The staff JWT (localStorage) — used by the api client to authorise
 *      QuoteStaff calls and to flag `is_staff` on public tracking.
 *   2. A per-session client id (sessionStorage) — a stable, client-generated
 *      id for debouncing view tracking and idempotent accept/decline (spec §8).
 */

const TOKEN_KEY = "arkitectly.staff.token";
const SESSION_KEY = "arkitectly.session.id";

/* ─── Staff token ──────────────────────────────────────────────────────── */

export function getStaffToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStaffToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable (private mode) — token stays in memory only */
  }
}

export function clearStaffToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

/** True when a staff token exists. Used to set `is_staff` on tracking so staff
 *  previews are excluded from client view counts (spec §8). */
export function isStaffSession(): boolean {
  return !!getStaffToken();
}

/* ─── Client session id ────────────────────────────────────────────────── */

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers.
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Stable per-tab session id, persisted for the session. */
export function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return generateId();
  }
}

/** A FRESH uid, minted per call — the engagement beacon's per-PAGE-LOAD session (api 333/334).
 *  Deliberately not the per-tab id above: a reload must start a new session row, or the
 *  cumulative MAX-merge would swallow every read after the first. */
export function newEngagementUid(): string {
  return generateId();
}
